import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';
import type { ReactNode } from 'react';
import {
  Header,
  ModalHeader as SemanticModalHeader,
  type ModalHeaderProps as SemanticModalHeaderProps,
} from 'semantic-ui-react';

import TruncateText from '@components/truncate-text';

import s from './modal-header.scss';

export interface ModalHeaderProps<
  TData = unknown,
> extends SemanticModalHeaderProps {
  /**
   * Icon rendered beside the title
   */
  icon?: ReactNode;
  /**
   * Primary heading text — when provided, enables the prop-driven layout
   */
  title?: string;
  /**
   * Subtitle rendered below the title (can be a function that receives modal data)
   */
  subtitle?: string | ((data: TData | undefined) => string | undefined);
  /**
   * Optional content anchored to the trailing (right) edge of the header
   */
  extra?: ReactNode;
  /**
   * Modal data (used when subtitle is a function)
   */
  data?: TData;
}

/**
 * Enhanced modal header that supports two usage modes:
 *
 * **Prop-driven** — structured layout with icon, title, subtitle, and extra slots:
 * ```tsx
 * <Modal.Header
 *   icon={<Icon name="plus" color="teal" />}
 *   title="Create Item"
 *   subtitle="Add a new item"
 * />
 * ```
 *
 * **Children** — backwards-compatible with Semantic UI's ModalHeader:
 * ```tsx
 * <Modal.Header>
 *   <Icon name="plus" /> Create Item
 * </Modal.Header>
 * ```
 */
export const ModalHeader = <TData = unknown,>({
  children,
  className,
  data,
  extra,
  icon,
  subtitle,
  title,
  ...rest
}: ModalHeaderProps<TData>) => {
  useStyles(s);

  // Children mode: pass through to Semantic's ModalHeader
  if (children || !title) {
    return (
      <SemanticModalHeader className={className} {...rest}>
        {children}
      </SemanticModalHeader>
    );
  }

  // Prop-driven mode: structured layout
  const resolvedSubtitle =
    typeof subtitle === 'function' ? subtitle(data) : subtitle;

  return (
    <SemanticModalHeader
      as={Header}
      className={classNames(s.header, className)}
      {...rest}
    >
      <div className={s.titleBlock}>
        {icon}
        <Header.Content>
          <h2>{title}</h2>
          {resolvedSubtitle && (
            <Header.Subheader className={s.subtitle}>
              <TruncateText position="top left">
                {resolvedSubtitle}
              </TruncateText>
            </Header.Subheader>
          )}
        </Header.Content>
      </div>
      {extra && <div className={s.extra}>{extra}</div>}
    </SemanticModalHeader>
  );
};
